import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { DjangoService } from '@core/services/django.service';
import { EnvironmentService } from '@core/services/environment.service';
import { IdleCallbackService } from '@core/services/idle-callback.service';
import { NavigationService } from '@core/services/navigation.service';
import { WINDOW } from '@core/services/window.service';
import { ImpersonationBannerComponent } from '@features/impersonation/components/impersonation-banner/impersonation-banner.component';
import { ImpersonationService } from '@features/impersonation/services/impersonation.service';
import { CookieConsentModalComponent } from '@features/modal/components/cookie-consent-modal/cookie-consent-modal.component';
import { ModalComponent } from '@features/modal/components/modal/modal.component';
import { interval, Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { NavigationSidebarService } from '../../services/navigation-sidebar.service';
import { DesktopFooterComponent } from '../desktop-footer/desktop-footer.component';
import { MainNavigationComponent } from '../main-navigation/main-navigation.component';
import { SidebarComponent } from '../sidebar/sidebar.component';

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        MainNavigationComponent,
        DesktopFooterComponent,
        SidebarComponent,
        ModalComponent,
        CookieConsentModalComponent,
        ImpersonationBannerComponent,
    ],
})
export class NavigationComponent implements OnInit, OnChanges, OnDestroy {
    private environmentService = inject(EnvironmentService);
    private navigationSidebarService = inject(NavigationSidebarService);
    private idleCallbackService = inject(IdleCallbackService);
    private djangoService = inject(DjangoService);
    navigationService = inject(NavigationService);
    private window = inject<Window>(WINDOW);

    isImpersonating = inject(ImpersonationService).isImpersonating;

    @Input() disable = false;
    @Input() additionalContainerClasses = [
        'mx-auto',
        'md:px-4 max-w-(--breakpoint-xl) 2xl:px-0',
    ];
    @Input() automaticFooterPadding = true;
    @Input() automaticNavbarPadding = true;
    @Input() footer = true;
    @Input() hideMainNavigationOnMobile = false;
    modalActive: Observable<boolean>;
    additionalClasses: string[] = [];
    private destroy = new Subject();

    ngOnDestroy(): void {
        this.destroy.next(null);
    }

    ngOnInit(): void {
        this.buildAdditionalClasses();

        if (
            this.environmentService.isBrowser() &&
            this.environmentService.isProduction() &&
            this.environmentService.getVersion() !== '1.0'
        ) {
            this.idleCallbackService
                .idleCallBack(10000)
                .pipe(
                    switchMap(() => interval(60 * 60 * 1000)),
                    switchMap(() => this.djangoService.version()),
                    filter(
                        version =>
                            version.version !==
                            this.environmentService.getVersion()
                    ),
                    takeUntil(this.destroy)
                )
                .subscribe(() => this.window.location.reload());
        }
    }

    closeSidebar() {
        this.navigationSidebarService.close();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.additionalContainerClasses ||
            changes.automaticFooterPadding
        ) {
            this.buildAdditionalClasses();
        }
    }

    buildAdditionalClasses() {
        this.additionalClasses = [...this.additionalContainerClasses];
        if (this.automaticFooterPadding) {
            this.additionalClasses.push('pb-36');
        }
    }
}
